// Plan 08-02 / Plan 08-07 — route table.
// Plan 15-10 — register 4 new Phase 15 social routes BEFORE the wildcard.
// Plan 30-03 — register DEV-only /dev/cell-swatch route behind
//              `import.meta.env.DEV` for Playwright baseline capture
//              (visual regression + reduced-motion + colorblind specs).
//              Vite tree-shakes the entire CellSwatchRoute + fixtures
//              import in prod builds — verified by the post-build dist
//              grep in Plan 30-03's verification.
//
// Routes per CONTEXT line 89 + UI-SPEC §"Phase 8 specialty surfaces":
//   /splash, /tutorial, /lobby, /match/{uuid}, /replay/{uuid}, /settings, /about
// Plus Phase 15 (Plan 15-10):
//   /profile/{uuid}, /leaderboard, /tournaments, /friends
// Plus Plan 30-03 (DEV-only):
//   /dev/cell-swatch
// Plus a fallback (root + unknown → splash).
//
// Plan 08-07 adds /about → AboutScreen and replaces the /settings stub with
// the real SettingsScreen (LanguageToggle + DeleteAccountModal trigger +
// accessibility slot for Plan 08-08's ReduceMotionToggle).
//
// Plan 15-10 adds /profile/{userId}, /leaderboard, /tournaments, /friends to
// land the Phase-15 social/competitive surface fully. The UUIDv4 regex on
// /profile mirrors the /match/{id} + /replay/{id} precedent so malformed
// URLs fall through to the wildcard splash (T-15-44 oracle-safe).

import { lazy, type ReactNode, Suspense } from 'react'
import { FormattedMessage } from 'react-intl'

// Plan 30-03 — DEV-only lazy import of CellSwatchRoute. The gate
// (`__DEV_ROUTES_ENABLED__`) is statically evaluable at build time, so
// Vite tree-shakes the lazy() call + the associated dynamic chunk in
// production builds where the gate is `false`.
//
// Why we don't use ONLY `import.meta.env.DEV`:
//   Playwright runs against `pnpm preview` (a production build served
//   locally). In that mode `import.meta.env.DEV` is `false` — so a
//   DEV-only gate would also strip the route from the Playwright test
//   harness. We compose `import.meta.env.DEV` (true in `pnpm dev`) with
//   the explicit `VITE_ENABLE_DEV_ROUTES=1` env var (which Playwright's
//   webServer command sets) so:
//     - Production deploy build (no VITE_ENABLE_DEV_ROUTES): gate=false,
//       route + import are tree-shaken (T-30-dev-route-leak mitigated).
//     - `pnpm dev` local development: gate=true via DEV, route mounted.
//     - Playwright preview build (with VITE_ENABLE_DEV_ROUTES=1):
//       gate=true, route mounted for spec capture.
//   Verified by post-build `grep -l 'CellSwatchRoute' apps/web/dist/`
//   returning zero JS-file matches under the production preset.
const __DEV_ROUTES_ENABLED__: boolean =
  import.meta.env.DEV ||
  import.meta.env.VITE_ENABLE_DEV_ROUTES === '1' ||
  import.meta.env.VITE_ENABLE_DEV_ROUTES === 'true'

const CellSwatchRouteLazy = __DEV_ROUTES_ENABLED__
  ? lazy(() =>
      import('../dev/CellSwatchRoute').then((m) => ({
        default: m.CellSwatchRoute,
      })),
    )
  : null

// Plan 31-06 — DEV-only /dev/phase-31/:archetype route for Playwright
// baseline capture of the 12 Phase 31 visual baselines (UI-SPEC §2).
// Same compositional gate as CellSwatchRouteLazy (D-30-03-02): prod builds
// without VITE_ENABLE_DEV_ROUTES tree-shake the lazy() call + chunk
// entirely.
const Phase31SurfaceRouteLazy = __DEV_ROUTES_ENABLED__
  ? lazy(() =>
      import('../dev/Phase31SurfaceRoute').then((m) => ({
        default: m.Phase31SurfaceRoute,
      })),
    )
  : null

// Plan 32-06 — DEV-only /dev/phase-32/:archetype route for Playwright
// baseline capture of the 16 Phase 32 visual baselines (UI-SPEC §2 —
// 4 themes × 4 archetypal surfaces). Same compositional gate as
// Phase31SurfaceRouteLazy: prod builds without VITE_ENABLE_DEV_ROUTES
// tree-shake the lazy() call + chunk entirely.
const Phase32SurfaceRouteLazy = __DEV_ROUTES_ENABLED__
  ? lazy(() =>
      import('../dev/Phase32SurfaceRoute').then((m) => ({
        default: m.Phase32SurfaceRoute,
      })),
    )
  : null

// Polish Phase 3 (PERF-05) — DEV-only /dev/perf particle perf/leak probe.
// Same compositional gate; tree-shaken from prod builds.
const PerfHarnessRouteLazy = __DEV_ROUTES_ENABLED__
  ? lazy(() =>
      import('../dev/PerfHarnessRoute').then((m) => ({
        default: m.PerfHarnessRoute,
      })),
    )
  : null

// VISUAL-PASS — DEV-only /dev/pieces piece-art gallery (all 6 silhouettes ×
// both slots + empower auras) for visual validation across the 4 themes.
// Same compositional gate; tree-shaken from prod builds.
const PieceGalleryRouteLazy = __DEV_ROUTES_ENABLED__
  ? lazy(() =>
      import('../dev/PieceGalleryRoute').then((m) => ({
        default: m.PieceGalleryRoute,
      })),
    )
  : null

import { AboutScreen } from '../ui/about/AboutScreen.js'
import { FriendsRoute } from '../ui/friends/FriendsRoute.js'
import { LeaderboardScreen } from '../ui/leaderboard/LeaderboardScreen.js'
import { PrivacyPolicy } from '../ui/legal/PrivacyPolicy.js'
import { LobbyScreen } from '../ui/lobby/LobbyScreen.js'
import { MatchScreen } from '../ui/match/MatchScreen.js'
import { ProfileScreen } from '../ui/profile/ProfileScreen.js'
import { ReplayScreen } from '../ui/replay/ReplayScreen.js'
import { SettingsScreen } from '../ui/settings/SettingsScreen.js'
import { SplashScreen } from '../ui/splash/SplashScreen.js'
import { TournamentsScreen } from '../ui/tournaments/TournamentsScreen.js'
import { TutorialScreen } from '../ui/tutorial/TutorialScreen.js'
import type { Route } from './router.js'

// ARCA-92 / WS1 — every route declares its `access` for the router-level auth
// guard (router.tsx). Taxonomy (from the accepted ARCA-91 plan):
//   - 'public': always open — /splash, /about, /privacy, /tutorial, DEV
//               routes, wildcard fallback.
//   - 'guest':  reachable by an anon-JWT session (no sign-in) — /match|/replay
//               for the current session's own matches. NOT gated here; the
//               anon JWT is minted on entering practice (WS2), not the guard.
//   - 'auth':   logged-out → redirect /splash — /lobby, /leaderboard,
//               /tournaments, /friends, /profile/{id}, /settings.
// /match & /replay stay 'guest' (not 'auth'): the router can't tell a guest's
// own bot match from a ranked one by URL alone, the backend already 401s
// protected data, and over-gating would break participant/share-link reach.
export const routes: Route[] = [
  {
    pattern: /^\/splash\/?$/,
    access: 'public',
    render: (): ReactNode => <SplashScreen />,
  },
  {
    pattern: /^\/tutorial\/?$/,
    access: 'public',
    render: (): ReactNode => <TutorialScreen />,
  },
  {
    pattern: /^\/lobby\/?$/,
    access: 'auth',
    render: (): ReactNode => <LobbyScreen />,
  },
  {
    // Plan 04 wires the real MatchScreen; Plan 05 will wire data flow to
    // drive setPlayerView. The route still expects a UUIDv4-shaped id.
    pattern: /^\/match\/(?<id>[0-9a-f-]{36})\/?$/,
    access: 'guest',
    render: (params): ReactNode => <MatchScreen matchId={params.id ?? ''} />,
  },
  {
    pattern: /^\/replay\/(?<id>[0-9a-f-]{36})\/?$/,
    access: 'guest',
    render: (params): ReactNode => <ReplayScreen id={params.id ?? ''} />,
  },
  {
    pattern: /^\/settings\/?$/,
    access: 'auth',
    render: (): ReactNode => <SettingsScreen />,
  },
  {
    pattern: /^\/about\/?$/,
    access: 'public',
    render: (): ReactNode => <AboutScreen />,
  },
  // Plan 09-02 — bilingual GDPR Art. 13/14 privacy policy at /privacy.
  // MUST stay before the wildcard or the wildcard swallows the route
  // (per 09-PATTERNS.md routes.tsx section).
  {
    pattern: /^\/privacy\/?$/,
    access: 'public',
    render: (): ReactNode => <PrivacyPolicy />,
  },
  // Plan 15-10 — Phase 15 social/competitive surface routes. All four MUST
  // stay before the wildcard (T-15-43 mitigation). The /profile UUIDv4 regex
  // mirrors /match/{id} so malformed paths fall through to splash without
  // leaking an "unknown user" oracle (T-15-44).
  {
    pattern: /^\/profile\/(?<userId>[0-9a-f-]{36})\/?$/,
    access: 'auth',
    render: (params): ReactNode => (
      <ProfileScreen userId={params.userId ?? ''} />
    ),
  },
  {
    pattern: /^\/leaderboard\/?$/,
    access: 'auth',
    render: (): ReactNode => <LeaderboardScreen />,
  },
  {
    pattern: /^\/tournaments\/?$/,
    access: 'auth',
    render: (): ReactNode => <TournamentsScreen />,
  },
  {
    pattern: /^\/friends\/?$/,
    access: 'auth',
    render: (): ReactNode => <FriendsRoute />,
  },
  // Plan 30-03 — DEV-only /dev/cell-swatch route for Playwright baseline
  // capture. The `__DEV_ROUTES_ENABLED__` gate + the lazy import are
  // tree-shaken from prod builds (no DEV + no VITE_ENABLE_DEV_ROUTES):
  // CellSwatchRouteLazy is null and this spread is the empty array.
  ...(__DEV_ROUTES_ENABLED__ && CellSwatchRouteLazy !== null
    ? [
        {
          pattern: /^\/dev\/cell-swatch\/?$/,
          access: 'public',
          render: (): ReactNode => (
            <Suspense
              fallback={
                <div>
                  <FormattedMessage
                    id="app.loading"
                    defaultMessage="Loading…"
                  />
                </div>
              }
            >
              <CellSwatchRouteLazy />
            </Suspense>
          ),
        } satisfies Route,
      ]
    : []),
  // Plan 31-06 — DEV-only /dev/phase-31/:archetype route. The archetype
  // path-segment is read inside Phase31SurfaceRoute via window.location.
  // Pattern accepts any non-slash segment; the route component validates
  // against the Phase31Archetype union and falls back to 'anchor-overlay'.
  ...(__DEV_ROUTES_ENABLED__ && Phase31SurfaceRouteLazy !== null
    ? [
        {
          pattern: /^\/dev\/phase-31\/([^/]+)\/?$/,
          access: 'public',
          render: (): ReactNode => (
            <Suspense
              fallback={
                <div>
                  <FormattedMessage
                    id="app.loading"
                    defaultMessage="Loading…"
                  />
                </div>
              }
            >
              <Phase31SurfaceRouteLazy />
            </Suspense>
          ),
        } satisfies Route,
      ]
    : []),
  // Plan 32-06 — DEV-only /dev/phase-32/:archetype route. The archetype
  // path-segment is read inside Phase32SurfaceRoute via window.location.
  // Pattern accepts any non-slash segment; the route component validates
  // against the Phase32Archetype union and falls back to 'flop-mid-cascade'.
  ...(__DEV_ROUTES_ENABLED__ && Phase32SurfaceRouteLazy !== null
    ? [
        {
          pattern: /^\/dev\/phase-32\/([^/]+)\/?$/,
          access: 'public',
          render: (): ReactNode => (
            <Suspense
              fallback={
                <div>
                  <FormattedMessage
                    id="app.loading"
                    defaultMessage="Loading…"
                  />
                </div>
              }
            >
              <Phase32SurfaceRouteLazy />
            </Suspense>
          ),
        } satisfies Route,
      ]
    : []),
  // Polish Phase 3 (PERF-05) — DEV-only /dev/perf particle perf/leak probe.
  ...(__DEV_ROUTES_ENABLED__ && PerfHarnessRouteLazy !== null
    ? [
        {
          pattern: /^\/dev\/perf\/?$/,
          access: 'public',
          render: (): ReactNode => (
            <Suspense
              fallback={
                <div>
                  <FormattedMessage
                    id="app.loading"
                    defaultMessage="Loading…"
                  />
                </div>
              }
            >
              <PerfHarnessRouteLazy />
            </Suspense>
          ),
        } satisfies Route,
      ]
    : []),
  // VISUAL-PASS — DEV-only /dev/pieces piece-art gallery.
  ...(__DEV_ROUTES_ENABLED__ && PieceGalleryRouteLazy !== null
    ? [
        {
          pattern: /^\/dev\/pieces\/?$/,
          access: 'public',
          render: (): ReactNode => (
            <Suspense
              fallback={
                <div>
                  <FormattedMessage
                    id="app.loading"
                    defaultMessage="Loading…"
                  />
                </div>
              }
            >
              <PieceGalleryRouteLazy />
            </Suspense>
          ),
        } satisfies Route,
      ]
    : []),
  // Root + unknown paths → splash (last-match wildcard).
  {
    pattern: /.*/,
    access: 'public',
    render: (): ReactNode => <SplashScreen />,
  },
]
